import { decomActions } from '../../../../shared/decom-actions';
import {
  DecomActionModel,
  DecomModel,
  DestUser,
  gPanel_Tier,
} from '../../../../../types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createDecomPolicy,
  editDecomPolicy,
  UserKeys,
} from '../../../../api/users-api';
import { Messages } from '../../../../utils/message-constants';
import { handleErrors } from '../../../../utils/errors';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import { useStore } from '../../../state/zustand-store';
import { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';
import { DecomForm } from '../decom-page.tsx';
import { DecomPolicyFormValues } from './decom-policy-form-layout.tsx';
import { DecomActionValues } from './decom-policy-form-schema.tsx';

export const getActionLabel = (value: string): string | undefined => {
  return decomActions.find((action) => action.value === value)?.label;
};

export interface DecomActionDaysModel extends Partial<DecomActionModel> {
  day: number;
}

export interface DecomSelectedActionsModel {
  day: number;
}

export type DecomFormField =
  | keyof DecomForm
  | `actionsData.${number}.${keyof DecomActionDaysModel}`;

export type ActionErrorEntry = {
  id: string;
  errors: Merge<FieldError, FieldErrorsImpl<any>>;
};

export interface DecomFormValues extends DecomModel {
  customDays?: number[];
}

export function createDecomModel(data: DecomFormValues): DecomModel {
  const decomModel = {
    ...data,
  };
  delete decomModel.customDays;
  return decomModel;
}

export function getDecomFormDefaultValues({
  pageMode,
  selectedDecom,
  loggedInUser,
}: {
  pageMode: string;
  selectedDecom?: Partial<DecomFormValues>;
  loggedInUser: string;
}): DecomFormValues {
  if ((pageMode === 'clone' || pageMode === 'edit') && selectedDecom) {
    const actionsObject = selectedDecom.actions ?? {};
    const customDays = Array.from(
      new Set(Object.values(actionsObject).flatMap(({ day }) => [day]))
    ).sort((a, b) => a - b);

    const baseValues: Partial<DecomFormValues> = {
      ...selectedDecom,
      name:
        pageMode === 'clone'
          ? `${selectedDecom.name}_Clone`
          : selectedDecom.name,
      createdBy:
        pageMode === 'clone'
          ? loggedInUser
          : selectedDecom.createdBy ?? loggedInUser,
      lastUpdatedBy: loggedInUser,
      lastUpdatedTime: Date.now(),
      actImmediately: selectedDecom.actImmediately ?? true,
      validateUserIsStillInDecomOrg:
        selectedDecom.validateUserIsStillInDecomOrg ?? false,
      validateUserIsStillSuspended:
        selectedDecom.validateUserIsStillSuspended ?? false,
      customDays: customDays ?? [],
      defaultUseManagerEmail:
        selectedDecom.defaultDestUser !== null
          ? false
          : selectedDecom.defaultUseManagerEmail
          ? true
          : null,
      notificationUser: selectedDecom.notificationUser ?? '',
    };
    if (pageMode === 'clone') {
      return {
        ...baseValues,
        id: Date.now().toString(),
        createdTime: Date.now(),
        org: [],
      } as unknown as DecomPolicyFormValues;
    }

    return baseValues as unknown as DecomPolicyFormValues;
  }

  return {
    defaultDestUser: null,
    defaultUseManagerEmail: null,
    defaultNotifyUser: false,
    defaultUserToNotify: [],
    name: '',
    org: [],
    notificationUser: '',
    createdBy: loggedInUser,
    createdTime: Date.now(),
    lastUpdatedBy: loggedInUser,
    lastUpdatedTime: Date.now(),
    keepUnsuspended: false,
    monitorSuspended: false,
    sendSummary: false,
    isActive: true,
    triggerType: 'suspension',
    userId: '',
    id: Date.now().toString(),
    actImmediately: true,
    validateUserIsStillInDecomOrg: false,
    validateUserIsStillSuspended: false,
    actions: { 0: { day: 0, actions: [] } },
    customDays: [0],
  };
}

export function useCreateDecomMutation(onSuccessCallbacks?: {
  onResetForm?: () => void;
  onCancel?: () => void;
  onClearChangeLog?: () => void;
  onResetStepIndex?: () => void;
}) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();

  return useMutation({
    mutationFn: (data: DecomModel & { changeLogMessage?: string }) =>
      createDecomPolicy(data, data.changeLogMessage ?? ''),
    onSuccess: (_returnData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Added('Decom policy'),
      });
      queryClient.setQueryData(
        [UserKeys.DecomModels],
        (oldData: DecomModel[]) => {
          if (!oldData) return;
          return [...oldData, data];
        }
      );
      createEvent(analytics.automation.decommissioning.create_decom_policy, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.decommissioning,
        triggerType: data.triggerType,
        keepUnsuspended: data.keepUnsuspended,
        sendSummary: data.sendSummary,
      });

      onSuccessCallbacks?.onClearChangeLog?.();
      onSuccessCallbacks?.onCancel?.();
      onSuccessCallbacks?.onResetForm?.();
      onSuccessCallbacks?.onResetStepIndex?.();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Adding('decom policy'),
      });
    },
  });
}

export function useEditDecomMutation(
  selectedDecomId?: string,
  onSuccessCallbacks?: {
    onResetForm?: () => void;
    onCancel?: () => void;
    onClearChangeLog?: () => void;
    onResetStepIndex?: () => void;
  }
) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();

  return useMutation({
    mutationFn: (data: DecomModel & { changeLogMessage?: string }) =>
      editDecomPolicy(data, data.changeLogMessage ?? ''),
    onSuccess: (_returnData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Decom policy'),
      });
      queryClient.setQueryData(
        [UserKeys.DecomModels],
        (oldData: DecomModel[]) => {
          if (oldData) {
            return oldData.map((model) => {
              if (model.id === selectedDecomId) {
                return data;
              } else {
                return model;
              }
            });
          } else {
            return [];
          }
        }
      );
      createEvent(analytics.automation.decommissioning.update_decom_policy, {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.decommissioning,
        triggerType: data.triggerType,
        keepUnsuspended: data.keepUnsuspended,
        sendSummary: data.sendSummary,
      });

      onSuccessCallbacks?.onClearChangeLog?.();
      onSuccessCallbacks?.onCancel?.();
      onSuccessCallbacks?.onResetForm?.();
      onSuccessCallbacks?.onResetStepIndex?.();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('decom policy'),
      });
    },
  });
}

function baseActionData(
  actionType: string,
  defaultNotifyUser: boolean,
  defaultUserToNotify: string[],
  defaultUseManagerEmail: boolean | null,
  defaultDestUser: DestUser | null
): DecomActionValues {
  return {
    actionType,
    destUser:
      defaultDestUser &&
      typeof defaultDestUser.userEmail === 'string' &&
      defaultDestUser.userEmail &&
      actionType !== 'delegateToRelation'
        ? [
            {
              userEmail: defaultDestUser.userEmail,
              role: defaultDestUser.role ?? 'owner',
              type: defaultDestUser.type ?? 'user',
            },
          ]
        : [],
    newOrgPath: null,
    notifyUser: defaultNotifyUser ?? false,
    userToNotify:
      defaultUserToNotify &&
      defaultUserToNotify.length > 0 &&
      defaultUserToNotify !== null
        ? defaultUserToNotify
        : [],
    startdate: 0,
    starttime: null,
    enddate: 0,
    endtime: null,
    getDeleted: null,
    query: null,
    exportType: null,
    matterId: null,
    createMatterOnDecom: false,
    matterNameTemplateCustomText: '',
    matterNameTemplateProfileField: '',
    matterNameTemplateDescription: '',
    matterRegion: 'ANY',
    corpus: null,
    dataScope: null,
    searchMethod: null,
    timeZone: null,
    terms: null,
    exportFormat: null,
    vaultStartTime: null,
    vaultEndTime: null,
    vaultVersionDate: null,
    vaultExcludeDrafts: false,
    vaultIncludeChatSpaces: false,
    vaultSharedDrivesOption: null,
    vaultPeopleQuery: null,
    vaultLocationQuery: null,
    vaultMinusWords: null,
    vaultResponseStatus: null,
    destFolderName: null,
    destFolderId: null,
    transferAction: null,
    subject: null,
    message: null,
    preName: null,
    postName: null,
    addDate: null,
    datePosition: null,
    mustAcknowledgeLogs: false,
    useManagerEmail: defaultUseManagerEmail ?? null,
    targetEmailType: null,
    customEmailType: null,
    useSharedDrive: null,
    newPassword: null,
    forceResetPassword: null,
    staticPassword: null,
    isSendPassword: null,
    daysUntilExpiration: null,
    emailSubject: null,
    emailContent: null,
    emailSendTo: null,
    emailCcTo: null,
    emailBccTo: null,
    sharingScope: null,
  };
}

export function createDefaultActionData(
  actionType: string,
  defaultNotifyUser: boolean,
  defaultUserToNotify: string[],
  defaultUseManagerEmail: boolean | null,
  defaultDestUser: DestUser,
  gPanelTier: gPanel_Tier
): DecomActionValues {
  const base = baseActionData(
    actionType,
    defaultNotifyUser,
    defaultUserToNotify,
    defaultUseManagerEmail,
    defaultDestUser
  );

  switch (actionType) {
    case 'UserNameChange':
      return {
        ...base,
        preName: '',
        postName: '',
        addDate: false,
        datePosition: 'prefix',
      };

    case 'UserOrgChange':
      return {
        ...base,
        newOrgPath: '',
      };

    case 'ResetPassword':
      return {
        ...base,
        forceResetPassword: false,
        staticPassword: false,
        newPassword: '',
      };

    case 'vacationResponder':
      return {
        ...base,
        subject: '',
        message: '',
      };

    case 'vaultGmailExport':
      return {
        ...base,
        corpus: 'MAIL',
        dataScope: 'ALL_DATA',
        searchMethod: 'ACCOUNT',
        timeZone: 'America/New_York',
        exportFormat: 'MBOX',
      };

    case 'vaultChatExport':
      return {
        ...base,
        corpus: 'HANGOUTS_CHAT',
        dataScope: 'ALL_DATA',
        searchMethod: 'ACCOUNT',
        timeZone: 'America/New_York',
        exportFormat: 'MBOX',
        vaultIncludeChatSpaces: false,
      };

    case 'vaultDriveExport':
      return {
        ...base,
        corpus: 'DRIVE',
        dataScope: 'ALL_DATA',
        searchMethod: 'ACCOUNT',
        timeZone: 'America/New_York',
        vaultSharedDrivesOption: 'NOT_INCLUDED',
        vaultVersionDate: null,
      };

    case 'vaultGeminiExport':
      return {
        ...base,
        corpus: 'GEMINI',
        dataScope: 'ALL_DATA',
        searchMethod: 'ACCOUNT',
        timeZone: 'America/New_York',
        exportFormat: 'XML',
      };

    case 'vaultGroupsExport':
      return {
        ...base,
        corpus: 'GROUPS',
        dataScope: 'ALL_DATA',
        searchMethod: 'ACCOUNT',
        timeZone: 'America/New_York',
      };

    case 'vaultCalendarExport':
      return {
        ...base,
        corpus: 'CALENDAR',
        dataScope: 'ALL_DATA',
        searchMethod: 'ACCOUNT',
        timeZone: 'America/New_York',
        exportFormat: 'ICS',
        vaultVersionDate: null,
        vaultPeopleQuery: [],
        vaultLocationQuery: [],
        vaultMinusWords: [],
        vaultResponseStatus: [],
      };

    case 'EmailDump':
      // setting manager as user for Gmail Export action with standard tier
      if (gPanelTier === 'standard') {
        return {
          ...base,
          getDeleted: false,
          query: null,
          exportType: 'FULL' as const,
          useManagerEmail: true,
          destUser: null,
        };
      }

      return {
        ...base,
        getDeleted: false,
        query: null,
        exportType: 'FULL' as const,
      };

    case 'sendEmail':
      return {
        ...base,
        emailSubject: '',
        emailContent: '',
        emailSendTo: [],
        emailCcTo: [],
        emailBccTo: [],
        targetEmailType: [],
        customEmailType: [],
      };

    case 'removeUsersShares':
      return {
        ...base,
        sharingScope: 'all' as const,
      };

    case 'transferDocs':
    case 'docsSharedExternallyReport':
    case 'actSharedExternallyReportMyDrive':
    case 'actSharedExternallyReportSharedDrive':
    case 'actSharedPubliclyReportMyDrive':
    case 'actSharedPubliclyReportSharedDrives':
    case 'removeExternalShares':
    case 'RemoveFromAllSharedDrive':
    case 'transfercals':
    case 'ReassignNicknames':
    case 'setForwarding':
    case 'addDelegate':
    case 'delegateToRelation':
      return base;

    default:
      return base;
  }
}

export function formatStringList(str: string[]) {
  if (str.length === 1) return str[0];
  if (str.length === 2) return `${str[0]} and ${str[1]}`;
  const clone = [...str];
  const last = clone.pop();
  return `${clone.join(', ')} and ${last}`;
}

export const USER_NAME_CHANGE_EXAMPLE_EMAIL = 'johndoe@company.com';
export const USER_NAME_CHANGE_DATE_FORMAT = 'YYYY-MM-DD';

// Mirrors UserNameChange.getNewUserName on the backend
export function getUserNameChangeExample(
  {
    preName,
    postName,
    addDate,
    datePosition,
  }: Partial<
    Pick<DecomActionValues, 'preName' | 'postName' | 'addDate' | 'datePosition'>
  >,
  date: string
): string {
  const [userName, domain] = USER_NAME_CHANGE_EXAMPLE_EMAIL.split('@');
  const withDate = (value?: string | null) =>
    value ? `${value}-${date}` : date;

  let prefix = preName;
  let suffix = postName;
  if (addDate) {
    if (datePosition === 'suffix') {
      suffix = withDate(suffix);
    } else {
      prefix = withDate(prefix);
    }
  }

  return [prefix, userName, suffix].filter(Boolean).join('.') + `@${domain}`;
}

export const destUserRequiredActions = [
  'transfercals',
  'transferDocs',
  'actSharedExternallyReportMyDrive',
  'actSharedExternallyReportSharedDrive',
  'actSharedPubliclyReportMyDrive',
  'actSharedPubliclyReportSharedDrives',
  'docsSharedExternallyReport',
  'removeExternalShares',
  'RemoveFromAllSharedDrive',
  'addDelegate',
  'setForwarding',
  'ReassignNicknames',
  'EmailDump',
  'delegateToRelation',
  'vaultGmailExport',
  'vaultCalendarExport',
  'vaultChatExport',
  'vaultDriveExport',
  'vaultGeminiExport',
  'vaultGroupsExport',
];
